import { Input, Popover, Space, Tag, Typography, message } from 'antd';
import type { InputRef } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useKnowledgeTagContext } from '@/hooks/knowledge-tag.store';
import { AddIcon as PlusOutlined, SearchIcon as SearchOutlined } from '@/icons';
import { createLogger } from '@/utils/logging';

const { Text } = Typography;

const logger = createLogger('TagManager', 'component');

/* ------------------------------------------------------------------ */
/*  Helpers — tag path normalisation (shared with backend convention)   */
/* ------------------------------------------------------------------ */

/**
 * Capitalise the first letter of every word in a string.
 * "data warehouse" → "Data Warehouse"
 */
const toTitleCase = (text: string): string =>
  text.replaceAll(/\b\w/g, (char: string) => char.toUpperCase());

/**
 * Normalise user input into a canonical tag path.
 *
 * Rules:
 *  - Strip leading `#` characters.
 *  - Treat `,` as `/` (hierarchy shorthand).
 *  - Keep only alphanumeric characters and spaces within each segment.
 *  - Collapse whitespace to a single space per segment.
 *  - Title Case every word ("data warehouse" → "Data Warehouse").
 *  - Rejoin segments with `/`.
 */
export const normalizeTagPath = (value: string): string => {
  const withoutHash = value.replace(/^#+/, '').trim();
  const slashReady = withoutHash.replaceAll(',', '/');
  return slashReady
    .split('/')
    .map((segment: string) =>
      toTitleCase(
        segment
          .replaceAll(/[^a-zA-Z0-9 ]/g, '')
          .replaceAll(/\s+/g, ' ')
          .trim(),
      ),
    )
    .filter(Boolean)
    .join('/');
};

const toUniqueLowercaseKey = (value: string): string => value.trim().toLowerCase();

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

/** Props for the `TagManager` hierarchical tag chip editor. */
export interface TagManagerProps {
  /** Current tag names assigned to this card. */
  tags: string[];
  /** Auth token for API calls. */
  accessToken: string;
  /** Scope identifiers. */
  clientId: string;
  projectId?: string | null;
  /** Card ID for persisting tag assignment/removal. Null when card is not yet persisted. */
  cardId?: string | null;
  /** Called after tags change (for parent state sync). */
  onTagsChange: (tags: string[]) => void;
  /** When true, operate in local-only mode (no API calls for assign/remove). */
  localOnly?: boolean;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

/** Hierarchical tag chip editor that supports create, assign, and remove against the API, with optional local-only mode. */
export const TagManager: React.FC<TagManagerProps> = ({
  tags,
  accessToken,
  clientId,
  projectId,
  cardId,
  onTagsChange,
  localOnly = false,
}) => {
  const {
    tags: availableTags,
    loading: tagsLoading,
    fetchTags,
    createTag,
    assignTagsToCard,
    removeTagFromCard,
    findTagByName,
  } = useKnowledgeTagContext();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(0);
  const inputRef = useRef<InputRef>(null);

  /**
   * Track every tag name the component has ever seen via the `tags` prop.
   * This ensures that tags loaded from the server (card data) remain
   * available in the dropdown even after the user removes them locally
   * before saving — the API may not return them if the fetch races or
   * the scope doesn't match.
   */
  const [knownTagNames, setKnownTagNames] = useState<Set<string>>(() => new Set(tags));

  useEffect(() => {
    setKnownTagNames((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const t of tags) {
        if (!next.has(t)) {
          next.add(t);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [tags]);

  /* Fetch available tags when dropdown opens (lazy load). */
  useEffect(() => {
    if (dropdownOpen && accessToken && clientId) {
      void fetchTags(accessToken, clientId, projectId ?? null);
    }
  }, [dropdownOpen, accessToken, clientId, projectId, fetchTags]);

  /* Focus input when dropdown opens. */
  useEffect(() => {
    if (dropdownOpen) {
      // Small delay so the popover renders before we focus.
      const timer = setTimeout(() => inputRef.current?.focus(), 60);
      return () => clearTimeout(timer);
    }
  }, [dropdownOpen]);

  /* Reset search and highlight when dropdown closes. */
  useEffect(() => {
    if (!dropdownOpen) {
      setSearchValue('');
      setHighlightIndex(0);
    }
  }, [dropdownOpen]);

  const normalizedSearch = normalizeTagPath(searchValue);

  /**
   * Build the dropdown option list by merging two sources:
   *  1. KnowledgeTag models from the store (fetched via API).
   *  2. Tag names the component has seen via props but that may not
   *     exist in the store yet (e.g. tags loaded with the card).
   *
   * This guarantees that a tag removed locally (before saving) still
   * appears in the dropdown so the user can re-add it.
   */
  const filteredOptions = useMemo(() => {
    const assignedSet = new Set(tags.map(toUniqueLowercaseKey));

    /* Start with store models, excluding currently-assigned tags. */
    const storeKeys = new Set<string>();
    const results: { tagId: string; tagName: string; usageCount: number }[] = [];

    for (const t of availableTags) {
      const key = toUniqueLowercaseKey(t.tagName);
      if (assignedSet.has(key)) {
        continue;
      }
      storeKeys.add(key);
      results.push({ tagId: t.tagId, tagName: t.tagName, usageCount: t.usageCount });
    }

    /* Merge in any known tag names not already covered by the store. */
    for (const name of knownTagNames) {
      const key = toUniqueLowercaseKey(name);
      if (assignedSet.has(key) || storeKeys.has(key)) {
        continue;
      }
      results.push({ tagId: `local-${key}`, tagName: name, usageCount: 0 });
    }

    /* Apply search filter and sort alphabetically. */
    const query = normalizedSearch.toLowerCase();
    return results
      .filter((r) => query.length === 0 || r.tagName.toLowerCase().includes(query))
      .sort((a, b) => a.tagName.localeCompare(b.tagName));
  }, [availableTags, tags, knownTagNames, normalizedSearch]);

  /* Show "Create" option when input doesn't exactly match existing tags. */
  const showCreateOption = useMemo(() => {
    if (normalizedSearch.length === 0) {
      return false;
    }
    const searchKey = toUniqueLowercaseKey(normalizedSearch);
    const exactMatch = filteredOptions.some((t) => toUniqueLowercaseKey(t.tagName) === searchKey);
    const alreadyAssigned = tags.some((t) => toUniqueLowercaseKey(t) === searchKey);
    return !exactMatch && !alreadyAssigned;
  }, [normalizedSearch, filteredOptions, tags]);

  /* Total selectable items (options + optional create row). */
  const totalItems = filteredOptions.length + (showCreateOption ? 1 : 0);

  /* ---- Actions --------------------------------------------------- */

  /** Optimistically adds an existing tag to the card and persists the assignment via the API. */
  const addTag = useCallback(
    async (tagName: string) => {
      const normalized = normalizeTagPath(tagName);
      if (!normalized) {
        return;
      }

      /* Deduplicate — do not add a tag that is already assigned. */
      if (tags.some((t) => toUniqueLowercaseKey(t) === toUniqueLowercaseKey(normalized))) {
        setSearchValue('');
        setDropdownOpen(false);
        return;
      }

      /* Optimistic local update. */
      const nextTags = [...tags, normalized];
      onTagsChange(nextTags);
      setSearchValue('');
      setDropdownOpen(false);

      if (localOnly || !cardId) {
        return;
      }

      try {
        await assignTagsToCard(accessToken, clientId, cardId, [normalized]);
      } catch (error) {
        /* Revert on failure. */
        onTagsChange(tags);
        const description = error instanceof Error ? error.message : 'Failed to add tag';
        void message.error(description);
        logger.error('Tag assignment failed', error);
      }
    },
    [tags, onTagsChange, localOnly, cardId, accessToken, clientId, assignTagsToCard],
  );

  /** Creates a new tag via the API, optimistically adds it locally, then assigns it to the card. */
  const createAndAddTag = useCallback(
    async (tagName: string) => {
      const normalized = normalizeTagPath(tagName);
      if (!normalized) {
        return;
      }

      /* Deduplicate — do not add a tag that is already assigned. */
      if (tags.some((t) => toUniqueLowercaseKey(t) === toUniqueLowercaseKey(normalized))) {
        setSearchValue('');
        setDropdownOpen(false);
        return;
      }

      /* Optimistic local update. */
      const nextTags = [...tags, normalized];
      onTagsChange(nextTags);
      setSearchValue('');
      setDropdownOpen(false);

      try {
        await createTag(accessToken, clientId, normalized, projectId ?? null);

        if (!localOnly && cardId) {
          await assignTagsToCard(accessToken, clientId, cardId, [normalized]);
        }
      } catch (error) {
        /* Revert on failure. */
        onTagsChange(tags);
        const description = error instanceof Error ? error.message : 'Failed to create tag';
        void message.error(description);
        logger.error('Tag creation failed', error);
      }
    },
    [
      tags,
      onTagsChange,
      localOnly,
      cardId,
      accessToken,
      clientId,
      projectId,
      createTag,
      assignTagsToCard,
    ],
  );

  /** Optimistically removes a tag from the card and calls the API to unassign it. */
  const removeTag = useCallback(
    async (tagName: string) => {
      /* Optimistic local update. */
      const nextTags = tags.filter((t) => t !== tagName);
      onTagsChange(nextTags);

      if (localOnly || !cardId) {
        return;
      }

      const tagModel = findTagByName(tagName);
      if (!tagModel) {
        return;
      }

      try {
        await removeTagFromCard(accessToken, clientId, cardId, tagModel.tagId);
      } catch (error) {
        /* Revert on failure. */
        onTagsChange(tags);
        const description = error instanceof Error ? error.message : 'Failed to remove tag';
        void message.error(description);
        logger.error('Tag removal failed', error);
      }
    },
    [
      tags,
      onTagsChange,
      localOnly,
      cardId,
      accessToken,
      clientId,
      findTagByName,
      removeTagFromCard,
    ],
  );

  /** Routes a dropdown item selection to either `createAndAddTag` (index 0 when create option is shown) or `addTag`. */
  const handleSelectItem = useCallback(
    (index: number) => {
      if (showCreateOption && index === 0) {
        void createAndAddTag(normalizedSearch);
        return;
      }
      const optionIndex = showCreateOption ? index - 1 : index;
      const selected = filteredOptions[optionIndex];
      if (selected) {
        void addTag(selected.tagName);
      }
    },
    [showCreateOption, normalizedSearch, filteredOptions, addTag, createAndAddTag],
  );

  /* ---- Keyboard navigation --------------------------------------- */

  /** Handles Arrow keys for list navigation, Enter/Tab to confirm selection, and Escape to close the dropdown. */
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setHighlightIndex((prev) => (prev + 1) % Math.max(totalItems, 1));
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setHighlightIndex((prev) => (prev - 1 + Math.max(totalItems, 1)) % Math.max(totalItems, 1));
        return;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        if (totalItems > 0) {
          handleSelectItem(highlightIndex);
        } else if (normalizedSearch.length > 0) {
          void createAndAddTag(normalizedSearch);
        }
        return;
      }
      if (event.key === 'Escape') {
        setDropdownOpen(false);
      }
    },
    [totalItems, highlightIndex, handleSelectItem, normalizedSearch, createAndAddTag],
  );

  /* ---- Dropdown content ------------------------------------------ */

  const dropdownContent = (
    <div className="tag-manager__dropdown" style={{ width: 300 }}>
      <Input
        ref={inputRef}
        aria-label="Search or create tag"
        placeholder="Add a new tag"
        prefix={<SearchOutlined style={{ color: '#999' }} />}
        value={searchValue}
        onChange={(e) => {
          setSearchValue(e.target.value);
          setHighlightIndex(0);
        }}
        onKeyDown={handleKeyDown}
        style={{ marginBottom: 8 }}
      />

      <div
        aria-label="Available tags"
        className="tag-manager__options"
        style={{ maxHeight: 240, overflowY: 'auto' }}
      >
        {showCreateOption && (
          <button
            type="button"
            aria-label={
              highlightIndex === 0
                ? `Create tag ${normalizedSearch} (highlighted)`
                : `Create tag ${normalizedSearch}`
            }
            className="tag-manager__option"
            style={{
              width: '100%',
              border: 'none',
              textAlign: 'left',
              padding: '6px 8px',
              cursor: 'pointer',
              borderRadius: 4,
              background: highlightIndex === 0 ? 'rgba(0,0,0,0.06)' : 'transparent',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
            onClick={() => handleSelectItem(0)}
            onMouseEnter={() => setHighlightIndex(0)}
          >
            <PlusOutlined style={{ fontSize: 12 }} />
            <Text>
              Create <Text strong>{normalizedSearch}</Text>
            </Text>
          </button>
        )}

        {filteredOptions.map((tag, i) => {
          const itemIndex = showCreateOption ? i + 1 : i;
          const isHighlighted = highlightIndex === itemIndex;
          return (
            <button
              key={tag.tagId}
              type="button"
              aria-label={isHighlighted ? `Tag ${tag.tagName} (highlighted)` : `Tag ${tag.tagName}`}
              className="tag-manager__option"
              style={{
                width: '100%',
                border: 'none',
                textAlign: 'left',
                padding: '6px 8px',
                cursor: 'pointer',
                borderRadius: 4,
                background: isHighlighted ? 'rgba(0,0,0,0.06)' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
              onClick={() => handleSelectItem(itemIndex)}
              onMouseEnter={() => setHighlightIndex(itemIndex)}
            >
              <Text>{tag.tagName}</Text>
              {tag.usageCount > 0 && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {tag.usageCount}
                </Text>
              )}
            </button>
          );
        })}

        {!tagsLoading && filteredOptions.length === 0 && !showCreateOption && (
          <Text type="secondary" style={{ display: 'block', padding: '8px', textAlign: 'center' }}>
            {normalizedSearch.length > 0 ? 'No matching tags' : 'No tags yet'}
          </Text>
        )}
      </div>

      <Text
        type="secondary"
        style={{ display: 'block', fontSize: 12, marginTop: 8, textAlign: 'center' }}
      >
        Use <Text code>/</Text> for nested tags <Text code>Tab</Text> to complete
      </Text>
    </div>
  );

  /* ---- Render ---------------------------------------------------- */

  return (
    <Space wrap size={[8, 8]} className="tag-manager" style={{ width: '100%' }}>
      {tags.map((tagName) => (
        <Tag
          key={tagName}
          closable
          onClose={(event) => {
            event.preventDefault();
            void removeTag(tagName);
          }}
        >
          {tagName}
        </Tag>
      ))}

      <Popover
        content={dropdownContent}
        trigger="click"
        open={dropdownOpen}
        onOpenChange={setDropdownOpen}
        placement="bottomLeft"
        arrow={false}
      >
        <Tag
          className="tag-manager__add-btn"
          style={{ borderStyle: 'dashed', cursor: 'pointer' }}
          aria-label="Add tag"
          aria-haspopup="listbox"
          onClick={() => setDropdownOpen(true)}
        >
          <PlusOutlined /> Add tag
        </Tag>
      </Popover>
    </Space>
  );
};

export default TagManager;
